// src/components/layout/NotificationBell.tsx
// ============================================================
// SALT — Notification Bell (Navbar)
//
// Renders a bell icon with unread count badge. Clicking opens
// the NotificationPanel dropdown. Only shown for authenticated
// users.
//
// Receives notification state from parent (Navbar) so the same
// hook data can be shared with MobileMenu for the mobile badge.
//
// Placement: Navbar right section, between social divider and
// avatar button.
// ============================================================

import React, { useRef, useState, useCallback, useEffect } from 'react';
import NotificationPanel from './NotificationPanel';
import type { Notification } from '../../types/notification.types';

// ── Props ───────────────────────────────────────────────────

interface NotificationBellProps {
  /** Whether to render (tie to isAuthenticated) */
  enabled: boolean;
  /** Unread count for badge */
  unreadCount: number;
  /** Loaded notifications for the panel */
  notifications: Notification[];
  /** Whether notifications are loading */
  isLoading: boolean;
  /** Called when panel opens — parent should trigger fetchNotifications */
  onPanelOpen: () => void;
  /** Mark a single notification as read */
  onMarkRead: (notificationId: number) => void;
  /** Mark all as read */
  /** Mark all as read */
  onMarkAllRead: () => void;
  /** Delete all notifications */
  onClearAll: () => void;
  /** Navigate to a notification's action URL */
  onNavigate: (url: string) => void;
}

// ── Component ───────────────────────────────────────────────

export default function NotificationBell({
  enabled,
  unreadCount,
  notifications,
  isLoading,
  onPanelOpen,
  onMarkRead,
  onMarkAllRead,
  onClearAll,
  onNavigate,
}: NotificationBellProps) {
  const bellRef = useRef<HTMLButtonElement>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // ── Toggle panel ──────────────────────────────────────────

  const handleToggle = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  // Trigger fetch after state update settles (avoids setState-during-render)
  useEffect(() => {
    if (panelOpen) {
      onPanelOpen();
    }
  }, [panelOpen, onPanelOpen]);

  const handleClose = useCallback(() => {
    setPanelOpen(false);
  }, []);

  // ── Navigate and close panel ──────────────────────────────

  const handleNavigate = useCallback(
    (url: string) => {
      setPanelOpen(false);
      onNavigate(url);
    },
    [onNavigate]
  );

  // Don't render anything for logged-out users
  if (!enabled) return null;

  // Format badge text: show "9+" for counts over 9
  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <>
      <button
        ref={bellRef}
        type="button"
        className="sl-notif-bell"
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={panelOpen}
      >
        {/* Bell SVG */}
        <svg
          className="sl-notif-bell-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span className="sl-notif-badge" aria-hidden="true">
            {badgeText}
          </span>
        )}
      </button>

      <NotificationPanel
        open={panelOpen}
        anchorRef={bellRef}
        onClose={handleClose}
        notifications={notifications}
        isLoading={isLoading}
        unreadCount={unreadCount}
        onMarkRead={onMarkRead}
        onMarkAllRead={onMarkAllRead}
        onClearAll={onClearAll}
        onNavigate={handleNavigate}
      />
    </>
  );
}